<template>
  <div class="initialsRoot" @click="handleClick" :class="{ large }">
    <div class="initialWrapper" :class="{ square }" :style="initialWrapperStyle">
      <div class="imageWrapper" v-if="iconValid">
        <img class="initialImage" :src="icon" @error="imageLoadError" />
      </div>
      <div v-else class="initials" :class="{ 'double': twoInitials, square }" :style="initialsStyle">
        {{ initialsText }}
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import { computed, ComputedRef, Ref, ref } from 'vue';

import { BrandColors } from '@/data/Branding';
import { getContrastColour } from '@/data/helpers/ColourContrastHelper';

interface Props {
  icon?: string;
  initials?: string;
  toInitialise?: string;
  color?: string;
  square?: boolean;
  large?: boolean;
}
const props = defineProps<Props>();

interface Emits {
  (e: 'click', event: MouseEvent): void,
}
const emit = defineEmits<Emits>();

const twoInitials: Ref<boolean> = ref(false);
const imageError: Ref<boolean> = ref(false);

const iconValid: ComputedRef<boolean> = computed(() => {
  return !!(props.icon && !imageError.value);
});

const bgColor: ComputedRef<string | null> = computed(() => {
  let bgColor = null;
  if (!iconValid.value) {
    bgColor = props.color || BrandColors.GREY;
  }
  return bgColor;
});

const initialsText: ComputedRef<string> = computed(() => {
  if (props.initials) {
    return props.initials;
  }

  let initials = '?';
  if (props.toInitialise) {
    let displayName = props.toInitialise.trim();
    if (displayName) {
      const initialCodePoint: number | undefined = displayName.codePointAt(0);
      // If it starts with a non-latin character, then just use that.
      if (initialCodePoint && initialCodePoint > 192) {
        initials = String.fromCodePoint(initialCodePoint);
      } else {
        displayName = displayName.replace(/[^a-zA-Z0-9]+/gim, ' ').trim();
        if (displayName) {
          initials = displayName.substring(0, 1);
          const spaceIndex = displayName.indexOf(' ');
          if (spaceIndex > 0) {
            initials += displayName.substring(spaceIndex + 1, spaceIndex + 2);
            // TODO: avoid mutating twoInitials ref here
            // eslint-disable-next-line vue/no-side-effects-in-computed-properties
            twoInitials.value = true;
          }
        }
      }
    }
  }
  return initials;
});

const initialsStyle: ComputedRef<{ 'color': string }> = computed(() => {
  const colour: string = bgColor.value ? getContrastColour(bgColor.value) : '#ffffff';
  return { color: colour };
});

const initialWrapperStyle:
ComputedRef<{ 'background-color'?: string | null; padding?: string | number; border?: string }> = computed(() => {
  return {
    'background-color': bgColor.value,
    padding: (props.icon && bgColor.value ? '0.2em' : 0),
  };
});

function imageLoadError(): void {
  imageError.value = true;
}

function handleClick(event: MouseEvent): MouseEvent {
  emit('click', event);
  return event;
}
</script>

<style scoped lang="scss">

.initialsRoot {
  // Set font size here to allow it to be overridden in the containing code and reproduced at different sizes
  font-size: $size-font-default;
  line-height: 1.4em;
  height: 2em;
  min-height: 2em;
  width: 2em;
  min-width: 2em;

  // When reproduced at a large size, we can afford the initials to be relatively smaller compared to the circle
  &.large {
    height: 2.5em;
    min-height: 2.5em;
    width: 2.5em;
    min-width: 2.5em;
  }
}

.initialWrapper {
  width: 100%;
  height: 100%;
  display: flex;
  align-items: center;
  justify-content: center;
  border-radius: $size-border-radius-primary;
  box-sizing: border-box;
  color: $colour-text-contrast;
  // TODO: Does this make sense?
  // I can't see a border colour being set, but we can get a small gap between the border and the image
  background-color: $colour-background-light;

  &.contrast {
    color: $colour-background-icon-contrast;
  }

  &:not(.square) {
    border-radius: 50%;
    overflow: hidden;
  }
}

.imageWrapper {
  position: relative;
  width: 100%;
  height: 100%;
  box-sizing: border-box;
}

.initialImage {
  position: absolute;
  top: 50%;
  left: 50%;
  transform: translate(-50%, -50%);
  vertical-align: middle;
  height: 100%;
  width: 100%;
  object-fit: cover;
  border-radius: $size-border-radius-primary;
  box-sizing: border-box;
}

.initials {
  text-transform: uppercase;
  border-radius: $size-border-radius-primary;
  user-select: none;
  display: flex;
  font-weight: $weight-normal;
}
</style>
